import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { useEffect, useRef, useState } from "react";
import Supercluster from "supercluster";
import config from "../../../config";
import useDrawingTool from "./DrawingTool";
import "./MapboxMap.css";

// TODO: Remove postinstall script from package.json when migrating away from CRA
// The mapbox-gl-csp-worker.js copy is a CRA workaround that won't be needed with direct webpack control add this comment to package.json

mapboxgl.workerUrl = "/mapbox-gl-csp-worker.js";
mapboxgl.accessToken = config.MAPBOX_ACCESS_TOKEN;

const MapboxMap = (props) => {
    const {
        center = { lat: 39.96, lng: -115.19 },
        zoom = 4,
        saveMapSearchSettings,
        places,
        requestId,
        searchCleared,
        mapSearchSettings,
        searchByArea,
        handleSearchCleared,
        clearFilters,
        disableDraw,
        mapCrumbCleared,
        savedPolygon,
        updateSearchPolygon,
        savedMapDetails,
        savedSearchRun,
    } = props;

    const mapContainer = useRef(null);
    const mapRef = useRef(null);
    const markersRef = useRef([]);
    const clusterIndexRef = useRef(null);
    const searchClearedRef = useRef(searchCleared);
    const resizeObserverRef = useRef(null);
    const popupOpenRef = useRef(false);
    const [mapReady, setMapReady] = useState(false);

    useEffect(() => {
        initializeSupercluster();
        initializeMap();
        setMapReady(true);

        return () => {
            cleanUp();
        };
    }, []);

    useDrawingTool({
        map: mapRef.current,
        disableDraw,
        mapReady,
        searchByArea,
        handleSearchCleared,
        clearFilters,
        mapCrumbCleared,
        savedPolygon,
        updateSearchPolygon,
        savedMapDetails,
        savedSearchRun,
    });

    useEffect(() => {
        searchClearedRef.current = searchCleared;
    }, [searchCleared]);

    useEffect(() => {
        if (mapRef.current?.loaded()) {
            loadClusterData();
        } else {
            mapRef.current.once("idle", () => loadClusterData());
        }
    }, [places]);

    useEffect(() => {
        if (searchCleared === true) {
            if (clusterIndexRef.current) {
                clusterIndexRef.current.load([]);
            }

            markersRef.current.forEach((marker) => marker.remove());
            markersRef.current = [];
        }
    }, [searchCleared]);

    const cleanUp = () => {
        if (mapRef.current) {
            const settings = {
                zoom: mapRef.current.getZoom(),
                center: { lat: mapRef.current.getCenter()?.lat, lng: mapRef.current.getCenter()?.lng },
                bounds: mapRef.current.getBounds(),
                mapTypeId: mapRef.current.isStyleLoaded() ? mapRef.current.getStyle()?.name : null,
                requestId: requestId,
            };
            saveMapSearchSettings(settings);
            mapRef.current.remove();
        }
        resizeObserverRef.current?.disconnect();
    };

    const initializeMap = () => {
        const styleMap = {
            "Mapbox Streets": "mapbox://styles/mapbox/streets-v12",
            "Mapbox Satellite Streets": "mapbox://styles/mapbox/satellite-streets-v12",
            "Mapbox Satellite": "mapbox://styles/mapbox/satellite-v9",
        };
        const initialStyle = styleMap[mapSearchSettings?.mapTypeId] || "mapbox://styles/mapbox/streets-v12";

        mapRef.current = new mapboxgl.Map({
            container: mapContainer.current,
            style: initialStyle,
            center: [center.lng, center.lat],
            zoom: zoom,
            scrollZoom: true,
            doubleClickZoom: true,
            dragPan: true,
            dragRotate: true,
            touchZoomRotate: true,
        });

        mapRef.current.addControl(new mapboxgl.ScaleControl(), "bottom-right");
        mapRef.current.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "bottom-right");
        mapRef.current.addControl(new mapboxgl.FullscreenControl(), "top-right");
        addMapTypeControl();

        mapRef.current.on("load", () => {
            loadClusterData();
        });

        mapRef.current.on("moveend", () => {
            if (clusterIndexRef.current && !popupOpenRef.current) {
                renderClusteredMarkers();
            }
        });

        resizeObserverRef.current = new ResizeObserver(() => mapRef.current?.resize());
        resizeObserverRef.current.observe(mapContainer.current);
    };

    const initializeSupercluster = () => {
        clusterIndexRef.current = new Supercluster({
            radius: 15,
            maxZoom: 16,
            minPoints: 2,

            reduce: (accumulated, props) => {
                accumulated.totalPlaces += props.totalPlaces;
            },

            initial: () => ({ totalPlaces: 0 }),

            map: (props) => ({ placeCount: props.placeCount || 0, totalPlaces: props.placeCount || 0 }),
        });
    };

    const addMapTypeControl = () => {
        const mapTypeControl = document.createElement("div");
        mapTypeControl.className = "mapboxgl-ctrl mapboxgl-ctrl-group";
        mapTypeControl.style.display = "flex";
        mapTypeControl.innerHTML = `
            <button id="streets-btn" title="Map" style="padding: 5px 10px; width:75px; font-weight: bold; color: black; font-size: large">Map</button>
            <div style="position: relative;">
                <button id="satellite-btn" title="Satellite" style="padding: 5px 10px; width:90px; font-size: large">Satellite</button>
                <div id="satellite-options" style="display: none; position: absolute; top: 100%; left: 0; width:85px; padding: 5px 0px 5px 5px; border-top: 1px solid #ddd; background: white; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
                    <label style="display: flex; align-items: center; cursor: pointer; font-size: 14px;">
                        <input type="checkbox" id="labels-checkbox" checked style="margin-right: 6px; cursor: pointer;">
                        Labels
                    </label>
                </div>
            </div>
        `;

        const streetsBtn = mapTypeControl.querySelector("#streets-btn");
        const satelliteBtn = mapTypeControl.querySelector("#satellite-btn");
        const satelliteOptions = mapTypeControl.querySelector("#satellite-options");
        const labelsCheckbox = mapTypeControl.querySelector("#labels-checkbox");
        let showLabels = true;

        const updateSatelliteStyle = () => {
            const style = showLabels
                ? { url: "mapbox://styles/mapbox/satellite-streets-v12", name: "Mapbox Satellite Streets" }
                : { url: "mapbox://styles/mapbox/satellite-v9", name: "Mapbox Satellite" };
            if (!mapRef.current.isStyleLoaded() || mapRef.current.getStyle()?.name !== style.name) {
                mapRef.current.setStyle(style.url);
            }
            mapRef.current.on("style.load", () => {
                renderClusteredMarkers();
            });
        };

        streetsBtn.onclick = () => {
            if (!mapRef.current.isStyleLoaded() || mapRef.current.getStyle()?.name !== "Mapbox Streets") {
                mapRef.current.setStyle("mapbox://styles/mapbox/streets-v12");
                streetsBtn.style.fontWeight = "bold";
                streetsBtn.style.color = "black";
                satelliteBtn.style.fontWeight = "normal";
                satelliteBtn.style.color = "";
                satelliteOptions.style.display = "none";
            }
        };

        satelliteBtn.onclick = () => {
            updateSatelliteStyle();
            satelliteBtn.style.fontWeight = "bold";
            satelliteBtn.style.color = "black";
            streetsBtn.style.fontWeight = "normal";
            streetsBtn.style.color = "";
            satelliteOptions.style.display = satelliteOptions.style.display === "none" ? "block" : "none";
        };

        labelsCheckbox.onchange = (e) => {
            showLabels = e.target.checked;
            updateSatelliteStyle();
        };

        mapRef.current.addControl(
            {
                onAdd: () => mapTypeControl,
                onRemove: () => {
                    streetsBtn.onclick = null;
                    satelliteBtn.onclick = null;
                    labelsCheckbox.onchange = null;
                    mapTypeControl.remove();
                },
            },
            "top-left"
        );
    };

    const loadClusterData = async () => {
        if (!clusterIndexRef.current) return;

        markersRef.current.forEach((marker) => marker.remove());
        markersRef.current = [];

        const features = await transformPlacesToGeoJSON();
        clusterIndexRef.current.load(features);

        if (!restoreMapSettingsIfNecessary()) {
            fitBoundsToMarkers();
        }
    };

    const transformPlacesToGeoJSON = async () => {
        if (!places || searchClearedRef.current) {
            return [];
        }

        const locationMap = new Map();
        places.forEach((place) => {
            if (place.latitude != null && place.longitude != null) {
                const latLng = `${place.latitude},${place.longitude}`;
                if (locationMap.has(latLng)) {
                    locationMap.get(latLng).push(place);
                } else {
                    locationMap.set(latLng, [place]);
                }
            }
        });

        const features = [];
        locationMap.forEach((placesAtLocation, latLng) => {
            const [lat, lng] = latLng.split(",").map(Number);

            features.push({
                type: "Feature",
                geometry: {
                    type: "Point",
                    coordinates: [lng, lat],
                },
                properties: {
                    placeCount: placesAtLocation.length,
                    places: placesAtLocation,
                    popupContent: props.markerLabelContentFunc
                        ? props.markerLabelContentFunc(placesAtLocation, props.isListing)
                        : "",
                },
            });
        });

        return features;
    };

    const renderClusteredMarkers = () => {
        if (!mapRef.current || !clusterIndexRef.current || searchClearedRef.current) {
            return;
        }

        markersRef.current.forEach((marker) => marker.remove());
        markersRef.current = [];

        const bounds = mapRef.current.getBounds();
        if (!bounds) return;

        const zoom = Math.floor(mapRef.current.getZoom());
        const lngPad = (bounds.getEast() - bounds.getWest()) * 0.2;
        const latPad = (bounds.getNorth() - bounds.getSouth()) * 0.2;
        const bbox = [
            bounds.getWest() - lngPad,
            bounds.getSouth() - latPad,
            bounds.getEast() + lngPad,
            bounds.getNorth() + latPad,
        ];

        let clusters;
        try {
            clusters = clusterIndexRef.current.getClusters(bbox, zoom);
        } catch (e) {
            return;
        }

        clusters.forEach((cluster) => {
            const [lng, lat] = cluster.geometry.coordinates;
            const { cluster: isCluster } = cluster.properties;
            if (isCluster) {
                renderClusterMarker(cluster, lng, lat);
            } else {
                renderIndividualMarker(cluster, lng, lat);
            }
        });
    };

    const restoreMapSettingsIfNecessary = () => {
        if (
            mapRef.current &&
            requestId &&
            mapSearchSettings?.requestId &&
            mapSearchSettings?.bounds &&
            mapSearchSettings?.requestId === requestId
        ) {
            mapRef.current.fitBounds(mapSearchSettings.bounds, { duration: 0 });
            saveMapSearchSettings(null);
            return true;
        }
        return false;
    };

    const renderClusterMarker = (cluster, lng, lat) => {
        const { totalPlaces } = cluster.properties;
        const clusterId = cluster.properties.cluster_id;

        const el = document.createElement("div");
        el.className = "cluster-marker";
        el.style.backgroundImage = "url(https://s3.us-east-2.amazonaws.com/connect.kidder.com/content/circlePin.svg)";
        el.style.width = "26px";
        el.style.height = "26px";
        el.style.backgroundSize = "100%";
        el.style.cursor = "pointer";
        el.style.display = "flex";
        el.style.alignItems = "center";
        el.style.justifyContent = "center";

        const label = document.createElement("div");
        label.textContent = totalPlaces;
        label.style.color = "#fff";
        label.style.fontSize = "12px";
        label.style.fontWeight = "bold";
        el.appendChild(label);

        const marker = new mapboxgl.Marker(el).setLngLat([lng, lat]).addTo(mapRef.current);

        el.addEventListener("click", () => {
            const leaves = clusterIndexRef.current.getLeaves(clusterId, Infinity);
            const bounds = new mapboxgl.LngLatBounds();
            leaves.forEach((leaf) => {
                bounds.extend(leaf.geometry.coordinates);
            });
            mapRef.current.fitBounds(bounds, { padding: 50, maxZoom: 18, duration: 200 });
        });

        el.addEventListener("mouseenter", () => {
            el.style.opacity = "0.8";
            el.style.transition = "opacity 0.2s";
        });

        el.addEventListener("mouseleave", () => {
            el.style.opacity = "1";
        });

        markersRef.current.push(marker);
    };

    const renderIndividualMarker = (cluster, lng, lat) => {
        const { popupContent, placeCount } = cluster.properties;

        const el = document.createElement("div");
        el.style.backgroundImage =
            "url(https://s3.us-east-2.amazonaws.com/connect.kidder.com/content/MarkerBlackBorder.svg)";
        el.style.width = "24px";
        el.style.height = "32px";
        el.style.backgroundSize = "100%";
        el.style.cursor = "pointer";

        if (placeCount > 1) {
            const label = document.createElement("div");
            label.textContent = placeCount;
            label.style.position = "absolute";
            label.style.top = "2px";
            label.style.left = "50%";
            label.style.transform = "translateX(-50%)";
            label.style.color = "#fff";
            label.style.fontSize = "12px";
            label.style.fontWeight = "bold";
            el.appendChild(label);
        }

        const marker = new mapboxgl.Marker(el).setLngLat([lng, lat]).addTo(mapRef.current);

        if (popupContent) {
            const popup = new mapboxgl.Popup({
                offset: 25,
                maxWidth: "none",
                anchor: "bottom",
            }).setHTML(popupContent);
            popup.on("open", () => {
                popupOpenRef.current = true;
                mapRef.current.panTo([lng, lat], { duration: 500, offset: [0, 100] });
            });
            popup.on("close", () => {
                popupOpenRef.current = false;
            });
            marker.setPopup(popup);
        }

        el.addEventListener("mouseenter", () => {
            el.style.backgroundImage =
                "url(https://s3.us-east-2.amazonaws.com/connect.kidder.com/content/MarkerWhiteBorder.svg)";
        });

        el.addEventListener("mouseleave", () => {
            el.style.backgroundImage =
                "url(https://s3.us-east-2.amazonaws.com/connect.kidder.com/content/MarkerBlackBorder.svg)";
        });

        markersRef.current.push(marker);
    };

    const fitBoundsToMarkers = () => {
        if (!places || places.length === 0) return;

        const bounds = new mapboxgl.LngLatBounds();

        places.forEach((place) => {
            if (place.latitude != null && place.longitude != null) {
                bounds.extend([place.longitude, place.latitude]);
            }
        });

        if (!bounds.isEmpty()) {
            if (savedPolygon?.geometry && !savedPolygon?.properties?.savedSearch) {
                mapRef.current.jumpTo({
                    center: savedPolygon?.viewport?.center,
                    zoom: savedPolygon?.viewport?.zoom,
                });
                renderClusteredMarkers();
            } else {
                mapRef.current.fitBounds(bounds, { padding: 50, maxZoom: 15, duration: 200 });
            }
        }
    };

    return <div ref={mapContainer} style={{ width: "100%", height: "100%", minHeight: "425px" }} />;
};

export default MapboxMap;
